// Authentication constants
export const TOKEN_KEY = "REACT_TOKEN_AUTH_KEY";

export const targetTypes = [
	{
		value: "behavioral",
		label: "Target Behaviorally",
		info: "Target users based on the content they have previously consumed on your site",
		preface: "Previously engaged content including:"
	},
	{
		value: "contextual",
		label: "Target Contextually",
		info: "Target specific content on your site",
		preface: "Content including:"
	}
];

export const targetingCriteria = [
	{
		value: "all",
		label: "All Site Content",
		info: "Target across all content on your site",
		isTaxonomy: false,
		isCustom: false
	},
	{
		value: "categories",
		label: "IAB Categories",
		info: "Target sites falling under specific content categories",
		isTaxonomy: false,
		isCustom: false,
	},
	{
		value: "keywords",
		label: "Keywords",
		info: "Target keywords or topics that match the content on your site",
		isTaxonomy: false,
		isCustom: false,
	},
	{
		value: "urls",
		label: "URLs",
		info: "Target specific sites",
		isTaxonomy: false,
		isCustom: false,
	},
	{
		value: "default",
		label: "Custom Content Taxonomy",
		info: "Target sites falling under specific criteria",
		isTaxonomy: true,
		isCustom: false,
	},
	{
		value: "garm",
		label: "Brand Safety + Suitability",
		info: "Target sites by brand safety risk level",
		isTaxonomy: false,
		isCustom: false,
	},
	{
		value: "sentiments",
		label: "Sentiment Targeting",
		info: "Target sites with specific sentiments",
		isTaxonomy: false,
		isCustom: false,
	}
];

export const engagementOptions = [
	{
		value: "pageViews",
		label: "Page Views",
		line: "Viewed",
		line2: "times or more in the past",
		line3: "days",
		isCustom: false,
		filters: []
	},
	{
		value: "timeSpent",
		label: "Time Spent on Page",
		line: "Spent",
		line2: "or more in the past",
		line3: "days",
		isCustom: false,
		filters: []
	},
];

// ASB-3720: "Ad Clicks" was pulled from the Content Consumption picker — its client-side counter
// never reaches Snowflake (forecast always 0) and the blur heuristic behind it over-counts ~10x.
// Kept here only so the ~11 existing cohorts still render and re-save; the picker offers it solely
// when the cohort being edited already uses it. Ad-click targeting lives in Custom User Data →
// Google Ad Manager → "Ad Clicks".
export const deprecatedEngagementOptions = [
	{
		value: "adClicks",
		label: "Ad Clicks (deprecated)",
		line: "Clicked",
		line2: "times or more in the past",
		line3: "days",
		isCustom: false,
		deprecated: true,
		filters: []
	},
];

export const urlConditions = [
	{ value: "equals", label: "URL is equal to" },
	{ value: "contains", label: "URL contains" },
];

export const os = [
	{ label: "Microsoft Windows", value: "windows" },
	{ label: "macOS", value: "macos" },
	{ label: "Linux", value: "linux" },
	{ label: "iOS", value: "ios" },
	{ label: "Android", value: "android" },
	{ label: "Other", value: "other" },
];

export const browser = [
	{ label: "Chrome", value: "chrome" },
	{ label: "Safari", value: "safari" },
	{ label: "Microsoft Edge", value: "edge" },
	{ label: "Firefox", value: "firefox" },
	{ label: "Opera", value: "opera" },
	{ label: "Other", value: "other" },
];

export const device = [
	{ label: "Desktop", value: "desktop" },
	{ label: "Mobile", value: "mobile" },
	{ label: "Tablet", value: "tablet" },
	{ label: "TV", value: "tv" },
];

export const techTranslations = {
	windows: "Windows",
	macos: "macOS",
	linux: "Linux",
	ios: "iOS",
	android: "Android",
	other: "Other",
	chrome: "Chrome",
	safari: "Safari",
	edge: "Edge",
	firefox: "Firefox",
	opera: "Opera",
	desktop: "Desktop",
	mobile: "Mobile",
	tablet: "Tablet",
	tv: "TV",
};

export const roles = [
	{ value: 1, label: "Admin" },
	{ value: 2, label: "Publisher Admin" },
	{ value: 3, label: "Publisher User" },
	{ value: 4, label: "Publisher Read-Only User"},
	{ value: 5, label: "Publisher Contributor" }
];

export const integrations = [
	{ label: "Google Ad Manager", disabled: false, id: "gam" },
	{ label: "PubMatic", disabled: true, id: "pubmatic" },
	{ label: "Magnite", disabled: true, id: "magnite" },
	{ label: "Index", disabled: true, id: "index" },
];

// The one standard user data integration, offered when the publisher's
// gamIntegrationEnabled setting is on (ASB-4149).
export const customUserDataGAMIntegration = { label: "Google Ad Manager", value: "gam", isCustom: false };

export const customUserDataGAMIntegrationMetrics = [
	{ label: "Ad Clicks", value: "clicks", line: "Clicked", line2: "times or more in the past", noun: "click", nounPlural: "clicks" },
	{ label: "Impressions Viewable", value: "impressionsViewable", line: "Viewed", line2: "times or more in the past", noun: "impression", nounPlural: "impressions" },
];

// ASB-3720: video / display split for the GAM integration. ArcTag's GAM listener never flags
// video; only the JW Player and STN Video integrations stamp `video: true` on their events, so
// "Video ads only" is empty on publishers without one of those integrations.
// `label` is the collapsed-summary chip; `inline` reads inside the sentence
// "Clicked [video or display] ads N times or more in the past D".
export const customUserDataGAMVideoOptions = [
	{ label: "Video and display ads", inline: "video or display", value: "any", video: null },
	{ label: "Video ads only", inline: "video", value: "video", video: true },
	{ label: "Display (non-video) ads only", inline: "display", value: "display", video: false },
];
export const customUserDataGAMDefaultLookback = 365;

export const amsFlavors = [
	{ label: "Full AMS", value: "full" },
	{ label: "AMS Contextual", value: "contextual" },
];

export const ttlOptions = [
	{ value: 1, label: '1 day' },
	{ value: 2, label: '2 days' },
	{ value: 3, label: '3 days' },
	{ value: 4, label: '4 days' },
	{ value: 5, label: '5 days' },
	{ value: 6, label: '6 days' },
	{ value: 7, label: '7 days' },
	{ value: 8, label: '8 days' },
	{ value: 9, label: '9 days' },
	{ value: 10, label: '10 days' },
	{ value: 11, label: '11 days' },
	{ value: 12, label: '12 days' },
	{ value: 13, label: '13 days' },
	{ value: 14, label: '14 days' },
	{ value: 15, label: '15 days' },
	{ value: 16, label: '16 days' },
	{ value: 17, label: '17 days' },
	{ value: 18, label: '18 days' },
	{ value: 19, label: '19 days' },
	{ value: 20, label: '20 days' },
	{ value: 21, label: '21 days' },
	{ value: 22, label: '22 days' },
	{ value: 23, label: '23 days' },
	{ value: 24, label: '24 days' },
	{ value: 25, label: '25 days' },
	{ value: 26, label: '26 days' },
	{ value: 27, label: '27 days' },
	{ value: 28, label: '28 days' },
	{ value: 29, label: '29 days' },
	{ value: 30, label: '1 month' },
	{ value: 60, label: '2 months' },
	{ value: 90, label: '3 months' },
	{ value: 120, label: '4 months' },
	{ value: 150, label: '5 months' },
	{ value: 180, label: '6 months' },
	{ value: 210, label: '7 months' },
	{ value: 240, label: '8 months' },
	{ value: 270, label: '9 months' },
	{ value: 300, label: '10 months' },
	{ value: 330, label: '11 months' },
	{ value: 365, label: '1 year' }
];

export const BULK_CONFIRM_ESCALATION_THRESHOLD = 50;

export const BULK_EDIT_CHUNK_SIZE = 200;

export const queryBuilderCombinators = [
	{ name: 'or', label: 'OR'},
	{ name: 'and', label: 'AND' }
];

export const queryBuilderTranslations = {
	addRule: {
	  label: `+ Add Filter`,
	  title: `Add Filter`,
	}
};

export const queryBuilderOperators = [
	{ name: '=', label: 'is' },
	{ name: '!=', label: 'is not' },
	{ name: '<', label: 'is less than' },
	{ name: '>', label: 'is greater than' },
	{ name: '<=', label: 'is less than or equal to' },
	{ name: '>=', label: 'is greater than or equal to' },
	{ name: 'contains', label: 'contains' },
	{ name: 'doesNotContain', label: 'does not contain' },
	{ name: 'beginsWith', label: 'begins with' },
	{ name: 'endsWith', label: 'ends with' },
	{ name: 'between', label: 'is between' },
];

export const queryBuilderDateOperators = [
	{ name: '=', label: 'is' },
	{ name: '!=', label: 'is not' },
	{ name: '<', label: 'is before' },
	{ name: '>', label: 'is after' },
	{ name: '<=', label: 'is on or before' },
	{ name: '>=', label: 'is on or after' },
	{ name: 'between', label: 'is between' },
];

export const queryBuilderBooleanOptions = [
	{value: true, label: "True"},
	{value: false, label: "False"}
];

export const ruleActions = [
	{value: "include", label: "Include Users Who Meet the Criteria"},
	{value: "exclude", label: "Exclude Users Who Meet the Criteria"}
];

export const timeUnits = [
	{value: "minutes", label: "minutes"},
	{value: "seconds", label: "seconds"}
];

export const preReleasePublishers = [
	"ArcSpan",
	"BC Media",
	"Bauer Media Group [Demo]",
	"Arc Weekly",
	"Kontext Korner",
	"Preview Post"
]

export const activationMethodTypeMap = [
	{value: "prebid", label: "Via Prebid"},
	{value: "adserver", label: "Ad Server"},
	{value: "cdp", label: "Customer Data Platform"},
	{value: "social", label: "Social"}
]

export const dataCenters = [
	{ label: "US", value: "us-east-1" },
	{ label: "EU", value: "eu-west-1" },
];